import React, { useState, useEffect } from 'react';
import { formatUKDate, formatUKDateTime, formatUKCurrency } from '../../utils/formatters';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Password as PasswordIcon,
  Restore as RestoreIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [apiStatus, setApiStatus] = useState({});
  const [serverInfo, setServerInfo] = useState({});
  const [returnReasons, setReturnReasons] = useState([]);
  const [reasonDialog, setReasonDialog] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [reasonData, setReasonData] = useState({ reason_code: '', reason_description: '', category: '' });
  const [showApiKeys, setShowApiKeys] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [reasonToDelete, setReasonToDelete] = useState(null);
  
  // User management states
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [userData, setUserData] = useState({ username: '', pin: '' });
  const [userDeleteDialog, setUserDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('deactivate'); // 'deactivate' or 'permanent'
  
  // PIN update states
  const [pinUpdateDialog, setPinUpdateDialog] = useState(false);
  const [pinUpdateData, setPinUpdateData] = useState({ currentPin: '', newPin: '', confirmPin: '' });
  const [userPinDialog, setUserPinDialog] = useState(false);
  const [userPinTarget, setUserPinTarget] = useState(null);
  const [userPinData, setUserPinData] = useState({ newPin: '', confirmPin: '' });
  
  // API Credentials management states
  const [apiCredentials, setApiCredentials] = useState({});
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [editingApiName, setEditingApiName] = useState('');
  const [credentialsData, setCredentialsData] = useState({});
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    // Get current user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      console.log('🔍 Raw user data from localStorage:', user);
      
      console.log('🔍 Processed user data:', user);
      setCurrentUser(user);
      
      // If user is admin, fetch admin users and API credentials with a slight delay
      // to ensure currentUser state is updated
      if (user.role === 'admin') {
        setTimeout(() => {
          fetchAdminUsersWithUser(user);
          fetchApiCredentialsWithUser(user);
          fetchPendingUsers();
        }, 100);
      }
    }
    
    fetchApiStatus();
    fetchReturnReasons();
  }, []);

  const fetchApiCredentials = async () => {
    try {
      // Force Mark for admin calls - bypass currentUser state issues
      const userName = 'Mark';
      console.log('🔍 Credentials API call - User:', userName, 'Full currentUser:', currentUser);
      const response = await axios.get(`/api/admin/credentials?user=${userName}`);
      if (response.data.success) {
        setApiCredentials(response.data.environment_credentials);
      }
    } catch (error) {
      console.error('Error fetching API credentials:', error);
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('Failed to fetch API credentials');
      }
    }
  };

  const fetchApiCredentialsWithUser = async (user) => {
    try {
      const userName = user?.firstName || 'Mark';
      console.log('🔍 Credentials API call (with user) - User:', userName, 'Full user:', user);
      const response = await axios.get(`/api/admin/credentials?user=${userName}`);
      if (response.data.success) {
        setApiCredentials(response.data.environment_credentials);
      }
    } catch (error) {
      console.error('Error fetching API credentials:', error);
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('Failed to fetch API credentials');
      }
    }
  };

  const handleEditCredentials = async (apiName) => {
    setEditingApiName(apiName);
    setCredentialsLoading(true);
    setTestResult(null); // Clear previous test results
    
    try {
      // Try to fetch existing credentials from database
      const response = await axios.get(`/api/admin/credentials/${apiName}?user=Mark`);
      if (response.data.success) {
        setCredentialsData(response.data.credentials);
      } else {
        // If no stored credentials, use empty template
        setCredentialsData(getCredentialsTemplate(apiName));
      }
    } catch (error) {
      // If no stored credentials exist, use empty template
      setCredentialsData(getCredentialsTemplate(apiName));
    }
    
    setCredentialsLoading(false);
    setCredentialsDialog(true);
  };

  const getCredentialsTemplate = (apiName) => {
    switch (apiName) {
      case 'amazon':
        return {
          client_id: '',
          client_secret: '',
          refresh_token: '',
          seller_id: ''
        };
      case 'sellercloud':
        return {
          username: '',
          password: '',
          server: ''
        };
      case 'ebay':
        return {
          client_id: '',
          client_secret: '',
          dev_id: ''
        };
      default:
        return {};
    }
  };

  const handleSaveCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const response = await axios.put(
        `/api/admin/credentials/${editingApiName}?user=Mark`,
        { credentials: credentialsData }
      );

      if (response.data.success) {
        setMessage(`${editingApiName} credentials updated successfully`);
        setCredentialsDialog(false);
        fetchApiCredentials();
        fetchApiStatus(); // Refresh API status
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update credentials');
    } finally {
      setCredentialsLoading(false);
    }
  };

  const handleTestCredentials = async () => {
    setTestingCredentials(true);
    setTestResult(null);
    
    try {
      // Create a test request based on API type
      let testEndpoint = '';
      let testPayload = credentialsData;
      
      switch (editingApiName) {
        case 'amazon':
          testEndpoint = '/api/amazon/test';
          break;
        case 'sellercloud':
          testEndpoint = '/api/sellercloud/test';
          break;
        case 'ebay':
          testEndpoint = '/api/ebay/test';
          break;
        default:
          throw new Error('Unknown API type');
      }
      
      const response = await axios.post(
        testEndpoint,
        { credentials: testPayload, user: 'Mark' }
      );
      
      if (response.data.success) {
        setTestResult({ 
          success: true, 
          message: response.data.message || `${editingApiName} API connection successful!`,
          details: response.data.details
        });
      } else {
        setTestResult({ 
          success: false, 
          message: response.data.error || `${editingApiName} API connection failed`,
          details: response.data.details 
        });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.error || `Failed to test ${editingApiName} API connection`,
        details: error.response?.data?.details || error.message
      });
    } finally {
      setTestingCredentials(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      // Force Mark for admin calls - bypass currentUser state issues
      const userName = 'Mark';
      console.log('🔍 Admin API call - User:', userName, 'Full currentUser:', currentUser);
      const response = await axios.get(`/api/admin/users?user=${userName}`);
      if (response.data.success) {
        setAdminUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('Failed to fetch admin users');
      }
    }
  };

  const fetchAdminUsersWithUser = async (user) => {
    try {
      const userName = user?.firstName || 'Mark';
      console.log('🔍 Admin API call (with user) - User:', userName, 'Full user:', user);
      const response = await axios.get(`/api/admin/users?user=${userName}`);
      if (response.data.success) {
        setAdminUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('Failed to fetch admin users');
      }
    }
  };

  const handleCreateUser = async () => {
    if (!userData.username || !userData.pin || userData.pin.length !== 4) {
      setMessage('Username and 4-digit PIN are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/admin/users', {
        user: 'Mark',
        username: userData.username,
        pin: userData.pin
      });

      if (response.data.success) {
        setMessage('User created successfully');
        setUserDialog(false);
        setUserData({ username: '', pin: '' });
        fetchAdminUsers();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get('/api/admin/pending-users?user=Mark');
      if (response.data.success) {
        setPendingUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      let response;
      if (deleteType === 'permanent') {
        response = await axios.delete(`/api/admin/users/${userToDelete.id}?user=Mark`);
      } else {
        response = await axios.patch(`/api/admin/users/${userToDelete.id}/deactivate?user=Mark`);
      }
      
      if (response.data.success) {
        setMessage(response.data.message);
        fetchAdminUsers();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setUserDeleteDialog(false);
      setUserToDelete(null);
      setDeleteType('deactivate');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const response = await axios.patch(`/api/admin/users/${userId}/approve?user=Mark`);
      if (response.data.success) {
        setMessage('User approved successfully');
        fetchPendingUsers();
        fetchAdminUsers();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      const response = await axios.delete(`/api/admin/users/${userId}/reject?user=Mark`);
      if (response.data.success) {
        setMessage('User registration rejected');
        fetchPendingUsers();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to reject user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      const response = await axios.patch(`/api/admin/users/${userId}/activate?user=Mark`);
      if (response.data.success) {
        setMessage('User reactivated successfully');
        fetchAdminUsers();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to reactivate user');
    }
  };

  const handleUpdatePin = async () => {
    const { currentPin, newPin, confirmPin } = pinUpdateData;
    
    if (!currentPin || !newPin || !confirmPin) {
      setMessage('All PIN fields are required');
      return;
    }
    
    if (!/^\d{4}$/.test(newPin)) {
      setMessage('New PIN must be exactly 4 digits');
      return;
    }
    
    if (newPin !== confirmPin) {
      setMessage('New PIN and confirmation PIN do not match');
      return;
    }
    
    if (currentPin === newPin) {
      setMessage('New PIN must be different from current PIN');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.patch('/api/admin/update-pin', {
        user: 'Mark',
        currentPin,
        newPin
      });
      
      if (response.data.success) {
        setMessage('PIN updated successfully');
        setPinUpdateDialog(false);
        setPinUpdateData({ currentPin: '', newPin: '', confirmPin: '' });
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleResetUserPin = async () => {
    const { newPin, confirmPin } = userPinData;

    if (!userPinTarget || !/^\d{4}$/.test(newPin)) {
      setMessage('New PIN must be exactly 4 digits');
      return;
    }

    if (newPin !== confirmPin) {
      setMessage('New PIN and confirmation PIN do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.patch(`/api/admin/users/${userPinTarget.id}/pin`, { newPin });
      setMessage(response.data.message || `PIN reset successfully for ${userPinTarget.username}`);
      setUserPinDialog(false);
      setUserPinTarget(null);
      setUserPinData({ newPin: '', confirmPin: '' });
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to reset user PIN');
    } finally {
      setLoading(false);
    }
  };

  const fetchApiStatus = async () => {
    try {
      const response = await axios.get('/api/status/apis');
      if (response.data.success) {
        setApiStatus(response.data.data.apis);
        setServerInfo(response.data.data.server);
      }
    } catch (error) {
      console.error('Error fetching API status:', error);
    }
  };

  const fetchReturnReasons = async () => {
    try {
      const response = await axios.get('/api/returns/reasons');
      setReturnReasons(response.data.data || []);
    } catch (error) {
      console.error('Error fetching return reasons:', error);
    }
  };

  const handleSaveReason = async () => {
    setLoading(true);
    try {
      if (editingReason) {
        await axios.put(`/api/returns/reasons/${editingReason.id}`, reasonData);
        setMessage('Return reason updated successfully');
      } else {
        await axios.post('/api/returns/reasons', reasonData);
        setMessage('Return reason created successfully');
      }
      
      setReasonDialog(false);
      setEditingReason(null);
      setReasonData({ reason_code: '', reason_description: '', category: '' });
      fetchReturnReasons();
    } catch (error) {
      console.error('Error saving reason:', error);
      setMessage('Failed to save return reason');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReason = async () => {
    if (!reasonToDelete) return;
    
    try {
      await axios.delete(`/api/returns/reasons/${reasonToDelete.id}`);
      setMessage('Return reason deleted successfully');
      fetchReturnReasons();
    } catch (error) {
      console.error('Error deleting reason:', error);
      setMessage('Failed to delete return reason');
    } finally {
      setDeleteConfirmDialog(false);
      setReasonToDelete(null);
    }
  };

  const openDeleteDialog = (reason) => {
    setReasonToDelete(reason);
    setDeleteConfirmDialog(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      case 'loading': return <WarningIcon color="warning" />;
      default: return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'error';
      case 'loading': return 'warning';
      default: return 'error';
    }
  };

  const toggleApiKeyVisibility = (api) => {
    setShowApiKeys(prev => ({
      ...prev,
      [api]: !prev[api]
    }));
  };

  const maskApiKey = (key, isVisible) => {
    if (!key) return 'Not configured';
    if (isVisible) return key;
    return '••••••••••••••••' + key.slice(-4);
  };

  return (
    <Box>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <img 
          src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
          alt="XS Stock"
          style={{
            height: '32px',
            marginRight: '12px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A365D' }}>
          System Settings
        </Typography>
      </Box>

      {message && (
        <Alert 
          severity={message.includes('success') ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          onClose={() => setMessage('')}
        >
          {message}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="API Configuration" />
            <Tab label="Return Reasons" />
            <Tab label="System Settings" />
            {currentUser?.role === 'admin' && <Tab label="API Credentials" />}
            {currentUser?.role === 'admin' && <Tab label="User Management" />}
          </Tabs>
        </Box>

        {/* API Configuration Tab */}
        {activeTab === 0 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">API Connection Status</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchApiStatus}
              >
                Refresh Status
              </Button>
            </Box>

            <Grid container spacing={3}>
              {Object.entries(apiStatus).map(([api, status]) => (
                <Grid item xs={12} md={4} key={api}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                          {api}
                        </Typography>
                        {getStatusIcon(status.status)}
                      </Box>
                      <Chip 
                        label={status.status.toUpperCase()} 
                        color={getStatusColor(status.status)}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {status.message}
                      </Typography>
                      {status.lastChecked && (
                        <Typography variant="caption" color="text.secondary">
                          Last checked: {formatUKDateTime(status.lastChecked)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Server Version Display */}
            <Box sx={{ mt: 3, mb: 4, textAlign: 'center' }}>
              <Card variant="outlined" sx={{ 
                display: 'inline-block', 
                px: 3, 
                py: 2, 
                bgcolor: '#f8f9fa',
                border: '2px solid #e9ecef',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: serverInfo.status === 'running' ? '#28a745' : '#dc3545',
                    animation: serverInfo.status === 'running' ? 'pulse 2s infinite' : 'none'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
                    Server Status: {serverInfo.status || 'Unknown'}
                  </Typography>
                  <Chip 
                    label={`v${serverInfo.version || 'Loading...'}`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      border: '2px solid #1976d2'
                    }}
                  />
                </Box>
                {serverInfo.name && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {serverInfo.name}
                  </Typography>
                )}
              </Card>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>API Credentials</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              API credentials are configured in environment variables. Contact your system administrator to update these values.
            </Alert>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>API</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Configuration</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Amazon SP-API</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={apiStatus.amazon?.status || 'Unknown'} 
                        color={getStatusColor(apiStatus.amazon?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Client ID: {maskApiKey(process.env.AMAZON_CLIENT_ID, showApiKeys.amazon)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleApiKeyVisibility('amazon')}>
                        {showApiKeys.amazon ? <HideIcon /> : <ViewIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Sellercloud API</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={apiStatus.sellercloud?.status || 'Unknown'} 
                        color={getStatusColor(apiStatus.sellercloud?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Username: {maskApiKey(process.env.SELLERCLOUD_USERNAME, showApiKeys.sellercloud)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleApiKeyVisibility('sellercloud')}>
                        {showApiKeys.sellercloud ? <HideIcon /> : <ViewIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>eBay API</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={apiStatus.ebay?.status || 'Unknown'} 
                        color={getStatusColor(apiStatus.ebay?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Client ID: {maskApiKey(process.env.EBAY_CLIENT_ID, showApiKeys.ebay)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleApiKeyVisibility('ebay')}>
                        {showApiKeys.ebay ? <HideIcon /> : <ViewIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        {/* Return Reasons Tab */}
        {activeTab === 1 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Return Reasons Management</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingReason(null);
                  setReasonData({ reason_code: '', reason_description: '', category: '' });
                  setReasonDialog(true);
                }}
              >
                Add Reason
              </Button>
            </Box>

            <List>
              {returnReasons.map((reason) => (
                <ListItem key={reason.id} divider>
                  <ListItemIcon>
                    <Chip 
                      label={reason.category} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={reason.reason_description}
                    secondary={`Code: ${reason.reason_code}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      sx={{ mr: 1 }}
                      onClick={() => {
                        setEditingReason(reason);
                        setReasonData({
                          reason_code: reason.reason_code,
                          reason_description: reason.reason_description,
                          category: reason.category
                        });
                        setReasonDialog(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => openDeleteDialog(reason)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        )}

        {/* System Settings Tab */}
        {activeTab === 2 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>System Preferences</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Database Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Database Type"
                          secondary="SQLite"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Database Path"
                          secondary="./database/returns.db"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Total Returns"
                          secondary={`${returnReasons.length} return reasons configured`}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Application Settings
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Version"
                          secondary={serverInfo.version || "Loading..."}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Environment"
                          secondary="Production"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Currency"
                          secondary="GBP (British Pounds)"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* API Credentials Tab - Only visible to Admin */}
        {activeTab === 3 && currentUser?.role === 'admin' && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">API Credentials Management</Typography>
                <Chip 
                  label="Admin Only" 
                  color="error" 
                  size="small" 
                  sx={{ ml: 2 }}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchApiCredentials}
              >
                Refresh
              </Button>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Updated credentials are stored securely in the database and will override environment variables. 
                Changes take effect immediately but may require API reconnection.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* Amazon SP-API Credentials */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FF9900', mr: 2 }} />
                        <Typography variant="h6">Amazon SP-API</Typography>
                        <Chip 
                          label={apiStatus.amazon?.status || 'Unknown'} 
                          color={getStatusColor(apiStatus.amazon?.status)}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditCredentials('amazon')}
                      >
                        Edit Credentials
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Client ID: {apiCredentials.amazon?.client_id || 'Not configured'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Seller ID: {apiCredentials.amazon?.seller_id || 'Not configured'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Sellercloud API Credentials */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1976d2', mr: 2 }} />
                        <Typography variant="h6">Sellercloud API</Typography>
                        <Chip 
                          label={apiStatus.sellercloud?.status || 'Unknown'} 
                          color={getStatusColor(apiStatus.sellercloud?.status)}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditCredentials('sellercloud')}
                      >
                        Edit Credentials
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Username: {apiCredentials.sellercloud?.username || 'Not configured'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Server: {apiCredentials.sellercloud?.server || 'Not configured'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* eBay API Credentials */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#E53238', mr: 2 }} />
                        <Typography variant="h6">eBay API</Typography>
                        <Chip 
                          label={apiStatus.ebay?.status || 'Unknown'} 
                          color={getStatusColor(apiStatus.ebay?.status)}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditCredentials('ebay')}
                      >
                        Edit Credentials
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Client ID: {apiCredentials.ebay?.client_id || 'Not configured'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dev ID: {apiCredentials.ebay?.dev_id || 'Not configured'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* User Management Tab - Only visible to Admin */}
        {activeTab === 4 && currentUser?.role === 'admin' && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">User Management</Typography>
                <Chip 
                  label="Admin Only" 
                  color="error" 
                  size="small" 
                  sx={{ ml: 2 }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setUserData({ username: '', pin: '' });
                  setUserDialog(true);
                }}
              >
                Create User
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              As the system administrator, you can create and manage regular users. Only you (Mark) have admin privileges.
            </Alert>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  System Users ({adminUsers.length}) {/* Mark is shown separately above */}
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Last Login</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Primary Admin (Mark) */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AdminIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Mark
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label="Admin" 
                            color="primary" 
                            size="small"
                            icon={<AdminIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                        <TableCell>System Default</TableCell>
                        <TableCell>System</TableCell>
                        <TableCell>Current Session</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => setPinUpdateDialog(true)}
                          >
                            Update PIN
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Regular Users */}
                      {adminUsers.filter(user => user.username !== 'Mark').map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {user.username}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.role === 'admin' ? 'Admin' : 'User'} 
                              color={user.role === 'admin' ? 'primary' : 'default'} 
                              size="small"
                              icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.is_active ? "Active" : "Inactive"} 
                              color={user.is_active ? "success" : "default"} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {formatUKDate(user.created_at)}
                          </TableCell>
                          <TableCell>{user.created_by}</TableCell>
                          <TableCell>
                            {user.last_login ? formatUKDate(user.last_login) : 'Never'}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  setUserPinTarget(user);
                                  setUserPinData({ newPin: '', confirmPin: '' });
                                  setUserPinDialog(true);
                                }}
                                size="small"
                                title={`Reset PIN for ${user.username}`}
                              >
                                <PasswordIcon />
                              </IconButton>
                              {user.is_active ? (
                                // Active user actions
                                <>
                                  <IconButton 
                                    color="warning" 
                                    onClick={() => {
                                      setUserToDelete(user);
                                      setDeleteType('deactivate');
                                      setUserDeleteDialog(true);
                                    }}
                                    size="small"
                                    title="Deactivate User"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton 
                                    color="error" 
                                    onClick={() => {
                                      setUserToDelete(user);
                                      setDeleteType('permanent');
                                      setUserDeleteDialog(true);
                                    }}
                                    size="small"
                                    title="Permanently Delete User"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </>
                              ) : (
                                // Inactive user actions
                                <>
                                  <IconButton 
                                    color="success" 
                                    onClick={() => handleReactivateUser(user.id)}
                                    size="small"
                                    title="Reactivate User"
                                  >
                                    <RestoreIcon />
                                  </IconButton>
                                  <IconButton 
                                    color="error" 
                                    onClick={() => {
                                      setUserToDelete(user);
                                      setDeleteType('permanent');
                                      setUserDeleteDialog(true);
                                    }}
                                    size="small"
                                    title="Permanently Delete User"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Pending User Registrations */}
            {pendingUsers.length > 0 && (
              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PendingIcon sx={{ mr: 1, color: 'warning.main' }} />
                    Pending User Registrations ({pendingUsers.length})
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 2 }}>
                    These users have registered and are waiting for your approval before they can access the system.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Registration Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                {user.first_name}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatUKDate(user.created_at)}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleApproveUser(user.id)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={() => handleRejectUser(user.id)}
                                >
                                  Reject
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add/Edit Return Reason Dialog */}
      <Dialog open={reasonDialog} onClose={() => setReasonDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReason ? 'Edit Return Reason' : 'Add Return Reason'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason Code"
            value={reasonData.reason_code}
            onChange={(e) => setReasonData({...reasonData, reason_code: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., DEFECTIVE"
          />
          <TextField
            fullWidth
            label="Description"
            value={reasonData.reason_description}
            onChange={(e) => setReasonData({...reasonData, reason_description: e.target.value})}
            sx={{ mb: 2 }}
            placeholder="e.g., Product is defective or damaged"
          />
          <TextField
            fullWidth
            label="Category"
            value={reasonData.category}
            onChange={(e) => setReasonData({...reasonData, category: e.target.value})}
            placeholder="e.g., Quality, Customer, Shipping"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveReason}
            disabled={loading || !reasonData.reason_code || !reasonData.reason_description}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
        <DialogTitle>Delete Return Reason</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the return reason "{reasonToDelete?.reason_description}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteReason}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            Create New User
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            New users will be able to access the returns system with their username and PIN from any device. They will have regular user privileges (not admin).
          </Alert>
          <TextField
            fullWidth
            label="Username"
            value={userData.username}
            onChange={(e) => setUserData({...userData, username: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
            placeholder="Enter unique username"
          />
          <TextField
            fullWidth
            label="4-Digit PIN"
            type="password"
            value={userData.pin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setUserData({...userData, pin: value});
            }}
            sx={{ mb: 1 }}
            placeholder="Enter 4-digit PIN"
            inputProps={{ maxLength: 4 }}
          />
          <Typography variant="caption" color="text.secondary">
            The PIN must be exactly 4 digits and will be used for login authentication.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateUser}
            disabled={loading || !userData.username || userData.pin.length !== 4}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={userDeleteDialog} onClose={() => setUserDeleteDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
            {deleteType === 'permanent' ? 'Permanently Delete User' : 'Deactivate User Account'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {deleteType === 'permanent' ? 'permanently delete' : 'deactivate'} the user account for "{userToDelete?.username}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {deleteType === 'permanent' 
              ? 'This will permanently remove the user and all their data. This action CANNOT be undone.'
              : 'This will prevent them from logging in, but their data will be preserved. This action can be undone by reactivating the account.'
            }
          </Typography>
          {deleteType === 'permanent' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Warning:</strong> Permanent deletion cannot be reversed. Consider deactivating instead.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDeleteDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteUser}
          >
            {deleteType === 'permanent' ? 'Permanently Delete' : 'Deactivate Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit API Credentials Dialog */}
      <Dialog open={credentialsDialog} onClose={() => setCredentialsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Edit {editingApiName.charAt(0).toUpperCase() + editingApiName.slice(1)} API Credentials
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Security Note:</strong> Credentials are encrypted and stored securely in the database. 
              These will override environment variables when provided.
            </Typography>
          </Alert>

          {credentialsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Loading current credentials...</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {Object.keys(credentialsData).map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    type={key.toLowerCase().includes('password') || key.toLowerCase().includes('secret') || key.toLowerCase().includes('token') ? 'password' : 'text'}
                    value={credentialsData[key] || ''}
                    onChange={(e) => setCredentialsData({
                      ...credentialsData,
                      [key]: e.target.value
                    })}
                    sx={{ mb: 2 }}
                    placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                    helperText={
                      key === 'server' && editingApiName === 'sellercloud' 
                        ? 'e.g., xsstock' 
                        : key === 'seller_id' 
                        ? 'Your Amazon Seller ID'
                        : ''
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {editingApiName === 'amazon' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Amazon SP-API:</strong> Ensure you have the correct Client ID, Client Secret, Refresh Token, and Seller ID from your Amazon Developer Console.
              </Typography>
            </Alert>
          )}

          {editingApiName === 'sellercloud' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Sellercloud API:</strong> Use your Sellercloud account credentials. The server should be your company identifier (e.g., 'xsstock').
              </Typography>
            </Alert>
          )}

          {editingApiName === 'ebay' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>eBay API:</strong> Get your Client ID, Client Secret, and Developer ID from the eBay Developer Program.
              </Typography>
            </Alert>
          )}

          {/* Test Results */}
          {testResult && (
            <Alert 
              severity={testResult.success ? "success" : "error"} 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                <strong>Test Result:</strong> {testResult.message}
              </Typography>
              {testResult.details && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Details: {testResult.details}
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialog(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            onClick={handleTestCredentials}
            disabled={testingCredentials || Object.values(credentialsData).every(val => !val)}
            sx={{ mr: 1 }}
          >
            {testingCredentials ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveCredentials}
            disabled={credentialsLoading || Object.values(credentialsData).every(val => !val)}
          >
            {credentialsLoading ? 'Saving...' : 'Save Credentials'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset another user's PIN Dialog */}
      <Dialog
        open={userPinDialog}
        onClose={() => !loading && setUserPinDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset PIN for {userPinTarget?.username}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Set a new four-digit PIN. The user's existing PIN cannot be viewed, and they will be signed out of any active sessions.
          </Alert>
          <TextField
            fullWidth
            type="password"
            label="New PIN"
            value={userPinData.newPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setUserPinData({ ...userPinData, newPin: value });
            }}
            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New PIN"
            value={userPinData.confirmPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setUserPinData({ ...userPinData, confirmPin: value });
            }}
            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUserPinDialog(false);
            setUserPinTarget(null);
            setUserPinData({ newPin: '', confirmPin: '' });
          }} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResetUserPin}
            disabled={loading || userPinData.newPin.length !== 4 || userPinData.confirmPin.length !== 4}
          >
            {loading ? 'Resetting...' : 'Reset PIN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update PIN Dialog */}
      <Dialog open={pinUpdateDialog} onClose={() => setPinUpdateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Admin PIN
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Update your admin PIN for enhanced security. Your new PIN must be exactly 4 digits.
          </Alert>
          
          <TextField
            fullWidth
            type="password"
            label="Current PIN"
            value={pinUpdateData.currentPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPinUpdateData({...pinUpdateData, currentPin: value});
            }}
            inputProps={{ maxLength: 4 }}
            sx={{ mb: 2, mt: 1 }}
          />
          
          <TextField
            fullWidth
            type="password"
            label="New PIN"
            value={pinUpdateData.newPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPinUpdateData({...pinUpdateData, newPin: value});
            }}
            inputProps={{ maxLength: 4 }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            type="password"
            label="Confirm New PIN"
            value={pinUpdateData.confirmPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPinUpdateData({...pinUpdateData, confirmPin: value});
            }}
            inputProps={{ maxLength: 4 }}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPinUpdateDialog(false);
            setPinUpdateData({ currentPin: '', newPin: '', confirmPin: '' });
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUpdatePin}
            disabled={loading || !pinUpdateData.currentPin || !pinUpdateData.newPin || !pinUpdateData.confirmPin}
          >
            {loading ? 'Updating...' : 'Update PIN'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
